import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { MdClose } from "react-icons/md";

import { useChannelUseForContactQuery } from "@hd/api/communication/queries/useChannelUseForContact";
import { useSendCommunicationMutation } from "@hd/api/communication/mutations/useSendCommunicationMutation";
import { useTranslate } from "@hd/i18n/useTranslate";
import { showErrorFeedback, showSuccessFeedback } from "@hd/toast/toasts";
import { ConversationChannelType } from "@hd/types/dto/shared/enums/conversation-channel-type";
import HdButton from "@hd/ui-kit/hd-button/HdButton";
import HdLoader from "@hd/ui-kit/hd-loader/HdLoader";
import HdModal from "@hd/ui-kit/hd-modal/HdModal";
import HdMultipleSelect from "@hd/ui-kit/hd-multiple-select/HdMultipleSelect";
import HdTabs from "@hd/ui-kit/hd-tabs/HdTabs";

import { buildChannelSelectModel } from "./channel-options";
import { CommunicationsModalTabIds } from "./communications-modal-tab-ids";
import { resolveCommunicationsModalTitle } from "./communications-modal-title";
import { useCommunicationsModalStore } from "./communications-modal.store";
import { buildChannelCommunicationsToSend, isSendDisabled } from "./send-payload";

const CommunicationEmailModalContent = lazy(() => import("./CommunicationEmailModalContent"));
const CommunicationWhatsappModalContent = lazy(() => import("./CommunicationWhatsappModalContent"));

function CommunicationsModal() {
  const t = useTranslate();

  const isOpen = useCommunicationsModalStore((state) => state.isOpen);
  const closeModal = useCommunicationsModalStore((state) => state.close);
  const companyId = useCommunicationsModalStore((state) => state.companyId);
  const contactId = useCommunicationsModalStore((state) => state.contactId);
  const communicationType = useCommunicationsModalStore(
    (state) => state.communicationType,
  );

  const [selectedTabId, setSelectedTabId] = useState<string>(
    CommunicationsModalTabIds.EMAIL,
  );
  const [selectedChannelIds, setSelectedChannelIds] = useState<
    (string | number)[]
  >([]);

  const sendCommunicationMutation = useSendCommunicationMutation();

  // solo il booleano, non il corpo: così non rirenderizzo a ogni tasto premuto
  const hasEmailBody = useCommunicationsModalStore(
    (state) => state.emailBody !== "",
  );

  const sendCommunication = async () => {
    // snapshot: le bozze non le sottoscrivo, altrimenti ogni tasto premuto
    // nell'editor rirenderizza tutta la modale
    const {
      groupId,
      companyId: sendCompanyId,
      contactId: sendContactId,
      entityDomain,
      entityId,
      actionCode,
      communicationType: sendCommunicationType,
      emailBody,
      emailRecipients,
      whatsappPhoneNumber,
    } = useCommunicationsModalStore.getState();

    try {
      if (!entityDomain || !entityId || sendCommunicationType == null) {
        throw new Error("Missing entity or communication type");
      }

      await sendCommunicationMutation.mutateAsync({
        groupId,
        companyId: sendCompanyId,
        contactId: sendContactId,

        entityDomain,
        entityId,

        actionCode,
        sendCommunications: [
          {
            type: sendCommunicationType,
            channelIds: buildChannelCommunicationsToSend(
              selectedChannelIds as ConversationChannelType[],
              { emailBody, emailRecipients, whatsappPhoneNumber },
              new Date().toISOString(),
            ),
          },
        ],
      });

      showSuccessFeedback(t("communicationsModal.sendSuccess"));
      closeModal();
    } catch {
      showErrorFeedback(t("communicationsModal.sendError"));
    }
  };

  const titleModel = useMemo(
    () => resolveCommunicationsModalTitle(communicationType),
    [communicationType],
  );

  const channelUseQuery = useChannelUseForContactQuery({
    companyId,
    contactId,
    communicationType,
  });

  const channelModel = useMemo(
    () => buildChannelSelectModel(channelUseQuery.data?.use),
    [channelUseQuery.data?.use],
  );

  const channelItems = useMemo(
    () =>
      channelModel.options.map((option) => ({
        id: option.id,
        name: t(option.labelKey),
      })),
    [channelModel.options, t],
  );

  // i canali di default arrivano dall'api, riparto da quelli ogni volta che il payload cambia
  useEffect(() => {
    setSelectedChannelIds(channelModel.preselectedIds);
  }, [channelModel.preselectedIds]);

  // riapro sempre sul tab email: resetto anche in chiusura, altrimenti alla
  // riapertura si vede per un attimo il tab di prima
  useEffect(() => {
    setSelectedTabId(CommunicationsModalTabIds.EMAIL);
  }, [isOpen]);

  return (
    <HdModal
      backdrop={true}
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          closeModal();
        }
      }}
      // closeOnClickOutside={false}
      className="p-2 bg-white min-w-[50vw] max-h-[95vh] flex flex-col"
    >
      <div className="bg-accent text-white flex p-4 items-center">
        <div className="text-md font-semibold">
          {titleModel.typeLabelKey
            ? t(titleModel.titleKey, { type: t(titleModel.typeLabelKey) })
            : t(titleModel.titleKey)}
        </div>
        <div className="ml-auto cursor-pointer" onClick={() => closeModal()}>
          <MdClose></MdClose>
        </div>
      </div>

      <HdTabs
        className="grow flex flex-col overflow-hidden mt-2"
        selectedKey={selectedTabId}
        onChange={setSelectedTabId}
      >
        <HdTabs.Header className="-mb-[1px] border-b">
          <HdTabs.Tab id={CommunicationsModalTabIds.EMAIL}>
            {t("communicationsModal.emailTab")}
          </HdTabs.Tab>
          <HdTabs.Tab id={CommunicationsModalTabIds.WHATSAPP}>
            {t("communicationsModal.whatsappTab")}
          </HdTabs.Tab>
        </HdTabs.Header>
        <HdTabs.Panel
          id={CommunicationsModalTabIds.EMAIL}
          className="grow flex flex-col overflow-auto"
        >
          <Suspense fallback={<HdLoader></HdLoader>}>
            <CommunicationEmailModalContent></CommunicationEmailModalContent>
          </Suspense>
        </HdTabs.Panel>
        <HdTabs.Panel
          id={CommunicationsModalTabIds.WHATSAPP}
          className="grow flex flex-col overflow-auto"
        >
          <Suspense fallback={<HdLoader></HdLoader>}>
            <CommunicationWhatsappModalContent></CommunicationWhatsappModalContent>
          </Suspense>
        </HdTabs.Panel>
      </HdTabs>

      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t">
        <div className="text-sm">{t("communicationsModal.channels")}</div>
        <HdMultipleSelect
          className="min-w-[220px]"
          items={channelItems}
          selectedValues={selectedChannelIds}
          disabledValues={channelModel.disabledIds}
          onChange={setSelectedChannelIds}
        ></HdMultipleSelect>
        <HdButton
          variant="primary"
          type="button"
          size="small"
          disabled={isSendDisabled(
            selectedChannelIds as ConversationChannelType[],
            sendCommunicationMutation.isPending,
            hasEmailBody,
          )}
          onClick={() => {
            sendCommunication();
          }}
        >
          {sendCommunicationMutation.isPending
            ? t("common.sending")
            : t("common.send")}
        </HdButton>
      </div>
    </HdModal>
  );
}

export default CommunicationsModal;
